import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SmartBookingLayout } from '../components/SmartBookingLayout';
import { StepCard } from '../components/StepCard';
import { SyncLoader } from 'react-spinners';
import { RootState } from '../redux/store';
import {
  setPatientDetails,
  setRequestMetadata,
  setTreatment,
  setPatientType,
  setPrimetimeFilter
} from '../redux/slices/appointmentSlice';
import { Button } from '../components/Button';
import { StepActions } from '../components/StepActions';
import Toggle from '../components/Toggle';
import { searchPatientDetails, createPatient, getUserFacingErrorMessage } from '../services/api';
import { PATIENT_TYPE_TO_BACKEND, PatientTypeEnum } from '../types';
import { AvailableSlots } from '../components/AvailableSlots';
import PreferredTimeSlots from '../components/PreferredTimeSlots';

const ERROR_MESSAGES = {
  INVALID_APPOINTMENT_TYPE: 'Invalid appointment_type. Please check the link and try again.',
  PATIENT_LOAD_FAILED: 'Failed to load patient information',
  PATIENT_SAVE_FAILED: 'Failed to save patient. Please try again.',
  CONTACT_SUPPORT: 'Please contact support if you need assistance.',
} as const;

export const SmartBooking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { practice, token } = useSelector((s: RootState) => s.auth);
  const { patientType, selectedSlot, patientDetails, request_id, primetimeFilter } = useSelector(
    (s: RootState) => s.appointment
  );

  const [loading, setLoading] = useState(true);
  const [savingPatient, setSavingPatient] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFormValid, setIsFormValid] = useState(false);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const appointmentTypeSlug = params.get('appointment_type');
  const patientId = params.get('patient_id');
  const isPracticeHasManualScheduler = practice?.scheduler_type === 'manual';


  // Initialize appointment type and patient details
  useEffect(() => {
    const initialize = async () => {
      if (!token || !practice) {
        setLoading(false);
        return;
      }

      if (isPracticeHasManualScheduler) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);      

      try {
        // Load patient details first: setting the patient type clears any selected treatment.
        if (patientId) {
          await loadPatientDetails(patientId);
        }

        // Validate and set appointment type
        if (appointmentTypeSlug) {
          const matchedAppointmentType = practice.appointment_types?.find(
            (at) => at.slug?.toLowerCase() === appointmentTypeSlug.toLowerCase()
          );

          if (!matchedAppointmentType) {
            setError(ERROR_MESSAGES.INVALID_APPOINTMENT_TYPE);
            return;
          }

          dispatch(setTreatment(matchedAppointmentType));
        }
      } catch (e: any) {
        setError(e?.message || ERROR_MESSAGES.PATIENT_LOAD_FAILED);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [token, practice, appointmentTypeSlug, patientId]);

  // Load patient details
  const loadPatientDetails = async (encryptedId: string) => {
    const response = await searchPatientDetails({ patient_encrypted_id: encryptedId });
    const patientData = response.data.attributes;

    dispatch(setPatientType(PATIENT_TYPE_TO_BACKEND.existing as PatientTypeEnum));
    dispatch(setPatientDetails({
      firstName: patientData.first_name || '',
      lastName: patientData.last_name || '',
      email: patientData.email || '',
      phone: patientData.phone_number || '',
      id: response.data.id,
      external_resource_id: patientData.external_resource_id,
      notes: '',
    }));
  };

  // Save patient and proceed
  const savePatient = async () => {
    if (isPracticeHasManualScheduler) {
      if (!isFormValid) return;
    } else {
      if (!selectedSlot) return;
    }

    if (request_id) {
      const paramsClone = new URLSearchParams(location.search);
      paramsClone.set('smart_booking', 'true');
      navigate(`../confirmation?${paramsClone.toString()}`);
      return;
    }

    setSavingPatient(true);
    try {
      const payload = {
        first_name: patientDetails.firstName,
        last_name: patientDetails.lastName,
        email: patientDetails.email,
        phone_number: patientDetails.phone,
        patient_type: patientType,
        patient_id: patientDetails.id,
        external_resource_id: patientDetails.external_resource_id,
      };

      const response = await createPatient(payload);
      const data = response.data;
      const attributes = data.attributes;
      dispatch(setRequestMetadata({
        request_id: data.id,
        requestable_id: attributes.requestable_id,
        requestable_type: attributes.requestable_type,
      }));
      // Navigate to confirmation
      const paramsClone = new URLSearchParams(location.search);
      paramsClone.set('smart_booking', 'true');
      navigate(`../confirmation?${paramsClone.toString()}`);
    } catch (error: any) {
      setError(getUserFacingErrorMessage(error, ERROR_MESSAGES.PATIENT_SAVE_FAILED));
    } finally {
      setSavingPatient(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <SmartBookingLayout>
        <div className="flex items-center justify-center min-h-[40vh]">
          <SyncLoader />
        </div>
      </SmartBookingLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <SmartBookingLayout>
        <StepCard>
          <div className="flex items-center justify-center min-h-[40vh] text-center">
            <div>
              <p className="text-lg font-medium text-red-600 mb-2">{error}</p>
              <p className="text-sm text-gray-500">{ERROR_MESSAGES.CONTACT_SUPPORT}</p>
            </div>
          </div>
        </StepCard>
      </SmartBookingLayout>
    );
  }

  return (
    <SmartBookingLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold text-black">When would you like to come?</h1>
            <p className="text-sm text-gray-500">
              Convenient evenings and weekends to fit your schedule
            </p>
          </div>
          {practice?.promote_primetime && (
            <div className="pr-2">
              <Toggle
                selected={primetimeFilter}
                onChange={(value) => dispatch(setPrimetimeFilter(value))}
              />
            </div>
          )}
        </div>

        {isPracticeHasManualScheduler ? (
          <PreferredTimeSlots onValidationChange={setIsFormValid} />
        ) : (
          <AvailableSlots />
        )}

        <StepActions className="justify-end">
          <Button
            title={savingPatient ? 'Saving...' : 'Continue'}
            disabled={(isPracticeHasManualScheduler ? !isFormValid : !selectedSlot) || savingPatient}
            onClick={savePatient}
          />
        </StepActions>
      </div>
    </SmartBookingLayout>
  );
};
